import '../styles/general.scss';
import '../styles/formField.scss';
import './input.scss';
import React, { useEffect, useState, useRef, memo, useCallback, createElement } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import _unescape from 'lodash/unescape';
import _debounce from 'lodash/debounce';
import MaskedInput from 'react-maskedinput';
import { maxInputLength, copyToClipboard, detectIsIosDevice, textInputTypes } from '../Constants';
import { Button, Spinner, Icon, useTheme } from '../..';
const isIOS = detectIsIosDevice();

// FilterInput / FormInput / SearchInput -----------------------
const Input = _ref => {
  let {
    name,
    dataName,
    focus,
    size,
    onFocus,
    onBlur,
    onChange,
    onKeyDown,
    onKeyUp,
    placeholder,
    type,
    value,
    disabled,
    isSetDisabled,
    onTabClick,
    noBlurWithoutValue,
    preventPaste,
    inputLength,
    className,
    floatingLabel,
    disabledError,
    error,
    mask,
    label,
    pattern,
    focusOnMount,
    readonly,
    loading,
    formatCharacters,
    onEdit,
    onClick,
    onCopy,
    deletable,
    onDelete,
    actionNode,
    autoComplete,
    url,
    showClearBtn,
    color,
    searchToggle,
    goBack,
    delayedSearchValue
  } = _ref;
  const {
    theme
  } = useTheme();
  const inputRef = useRef(null);
  const isDefaultType = type === 'default' || type === 'simple' || !type;
  const isSearchType = type === 'search';
  const isFormType = type === 'text' || type === 'password' || type === 'email' || type === 'copy' || type === 'editable' || type === 'delete' || type === 'number' || type === 'tel';
  const withBtn = type === 'password' || type === 'editable' || type === 'copy' || type === 'delete' || deletable;
  const [internalValue, setInternalValue] = useState(value || '');
  const [showValue, setShowValueStatus] = useState(false);
  let activeState = false;
  useEffect(() => {
    return () => {
      if (isIOS && activeState) window.dispatchEvent(new Event('keyboardDidHide'));
    };
  }, []);
  useEffect(() => {
    if (focus) inputRef.current.focus();
  });
  useEffect(() => {
    if (isSetDisabled) handleAutoBlur();else if (inputRef.current && !mask) {
      inputRef.current.removeAttribute('disabled');
      if (focus) inputRef.current.focus();
    }
  }, [isSetDisabled]);
  useEffect(() => {
    setInternalValue(value);
  }, [value]);
  useEffect(() => {
    if (focusOnMount) inputRef.current.focus();
    return () => {
      if (isIOS && activeState) window.dispatchEvent(new Event('keyboardDidHide'));
    };
  }, [focusOnMount]);
  const delayedQuery = useCallback(_debounce(query => {
    if (onChange) onChange(query);
  }, delayedSearchValue), [searchToggle]);
  const handleOnChange = e => {
    setInternalValue(e.target.value);
    if (isSearchType) {
      delayedQuery(e.target.value);
      return;
    }
    if (isFormType && (e.target.value === '' || !pattern || pattern.test(e.target.value))) {
      if (onChange) onChange(e.target.value);
      return;
    }
    if (onChange) onChange(e.target.value);
  };
  const handleOnKeyDown = e => {
    if (isFormType) {
      if (onKeyDown) onKeyDown(e);
      return;
    }
    if (e.repeat) return;
    if (!onKeyDown && !onTabClick) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      if (onKeyDown && typeof onKeyDown === 'function') onKeyDown();
      if (!noBlurWithoutValue) handleAutoBlur();
    }
    if (e.key === 'Tab' && onTabClick) onTabClick();
  };
  const handleOnKeyUp = e => {
    if (!onKeyUp) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      onKeyUp();
      if (!noBlurWithoutValue) handleAutoBlur();
    }
  };
  const handleAutoBlur = () => {
    if (inputRef && inputRef.current) {
      inputRef.current.blur();
      inputRef.current.setAttribute('disabled', true);
    }
  };
  const handleOnBlur = e => {
    activeState = false;
    if (isIOS && !readonly) window.dispatchEvent(new Event('keyboardDidHide'));
    if (onBlur) onBlur(e);
  };
  const handleOnFocus = e => {
    activeState = true;
    if (isIOS && !readonly) window.dispatchEvent(new Event('keyboardDidShow'));
    if (onFocus) onFocus(e);
  };
  const handleOnClick = e => {
    if (onClick) onClick(e);
  };
  const handleGoBack = () => {
    if (isIOS && !readonly) window.dispatchEvent(new Event('keyboardDidHide'));
    if (goBack) goBack();
  };
  const deleteSearchValue = e => {
    e.preventDefault();
    e.stopPropagation();
    delayedQuery('');
    setInternalValue('');
    if (inputRef.current) inputRef.current.focus();
  };
  const showHideValue = () => setShowValueStatus(!showValue);
  const handleOnEdit = () => onEdit && onEdit(name);
  const handleDelete = () => onDelete && onDelete(name);
  const handleCopy = () => copyToClipboard(inputRef, type, url, onCopy);
  return /*#__PURE__*/React.createElement(React.Fragment, null, isDefaultType && /*#__PURE__*/React.createElement("input", {
    ref: inputRef,
    className: classNames(type ? `wrts-${type}-input` : null, className, color, theme, 'notranslate'),
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onChange: handleOnChange,
    onKeyDown: handleOnKeyDown,
    onKeyUp: handleOnKeyUp,
    placeholder: placeholder,
    disabled: disabled,
    type: "text",
    value: _unescape(internalValue),
    "data-name": dataName || name,
    size: size,
    onPaste: e => {
      if (preventPaste) e.preventDefault();
    },
    maxLength: inputLength || maxInputLength
  }), isFormType && /*#__PURE__*/React.createElement("div", {
    className: classNames('wrts-form-field', className, theme, {
      'wrts-copy-field': type === 'copy'
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames('wrts-input-field notranslate', theme, {
      error,
      'hide-placeholder': floatingLabel,
      'hide-label': !floatingLabel,
      grey: color === 'grey',
      'with-btn': withBtn || actionNode
    })
  }, url && /*#__PURE__*/React.createElement("span", {
    className: "prefilled-val"
  }, url, "/"), /*#__PURE__*/createElement(mask ? MaskedInput : 'input', {
    autoComplete: autoComplete || '',
    placeholder: floatingLabel ? '*' : label,
    className: classNames('wrts-input', theme, {
      error,
      grey: color === 'grey',
      'hide-label': !floatingLabel
    }),
    name,
    onFocus: handleOnFocus,
    onBlur: handleOnBlur,
    onChange: handleOnChange,
    onKeyDown: handleOnKeyDown,
    onClick: handleOnClick,
    type: showValue && type === 'password' || type === 'editable' || type === 'delete' || type === 'copy' ? 'text' : type,
    value: internalValue,
    readOnly: readonly,
    disabled,
    ref: inputRef,
    'data-name': name,
    mask,
    formatCharacters,
    maxLength: inputLength || maxInputLength,
    inputMode: textInputTypes.has(type) ? 'text' : type === 'number' ? 'numeric' : type
  }), floatingLabel && /*#__PURE__*/React.createElement("label", null, label), type === 'password' && internalValue && !loading && /*#__PURE__*/React.createElement(Button, {
    type: "icon-link",
    iconName: `visibility${!showValue ? '-off' : ''}`,
    className: "field-btn",
    onClick: showHideValue,
    btnType: "button"
  }), type === 'editable' && !loading && /*#__PURE__*/React.createElement(Button, {
    type: "icon-link",
    iconName: "create",
    className: "field-btn",
    onClick: handleOnEdit,
    btnType: "button"
  }), type === 'copy' && !loading && /*#__PURE__*/React.createElement(Button, {
    type: "icon-link",
    iconName: "copy",
    className: "field-btn",
    onClick: handleCopy,
    btnType: "button"
  }), (type === 'delete' || deletable) && !loading && /*#__PURE__*/React.createElement(Button, {
    type: "icon-link",
    iconName: "trash-can",
    className: "field-btn",
    onClick: handleDelete,
    tabIndex: "-1",
    btnType: "button"
  }), actionNode, loading && /*#__PURE__*/React.createElement(Spinner, null)), !disabledError && /*#__PURE__*/React.createElement("span", {
    className: "error-msg",
    "data-name": name
  }, error)), isSearchType && /*#__PURE__*/React.createElement("div", {
    className: classNames('wrts-search-field', className, theme)
  }, /*#__PURE__*/React.createElement("input", {
    ref: inputRef,
    className: classNames('wrts-input notranslate', {
      'active-back': !!goBack,
      'active-clear': internalValue && showClearBtn
    }, color, theme),
    type: "text",
    placeholder: placeholder,
    value: internalValue,
    onChange: handleOnChange,
    onClick: handleOnClick,
    onFocus: handleOnFocus,
    size: size,
    onBlur: handleOnBlur,
    "data-name": dataName || 'search_input',
    maxLength: inputLength || maxInputLength
  }), /*#__PURE__*/React.createElement(Icon, {
    name: "search"
  }), /*#__PURE__*/React.createElement(Button, {
    type: "icon-link",
    className: "back-btn",
    iconName: "arrow-back",
    onClick: handleGoBack,
    btnType: "button"
  }), /*#__PURE__*/React.createElement(Button, {
    type: "icon-link",
    className: "close-btn",
    iconName: "close",
    onClick: deleteSearchValue,
    btnType: "button"
  })));
};
Input.defaultProps = {
  delayedSearchValue: 400,
  showClearBtn: true
};
Input.propTypes = {
  name: PropTypes.string,
  dataName: PropTypes.string,
  focus: PropTypes.bool,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onKeyDown: PropTypes.func,
  onKeyUp: PropTypes.func,
  placeholder: PropTypes.string,
  type: PropTypes.oneOf(['default', 'simple', 'text', 'password', 'email', 'copy', 'editable', 'delete', 'number', 'tel', 'search']).isRequired,
  value: PropTypes.string,
  disabled: PropTypes.bool,
  isSetDisabled: PropTypes.bool,
  onTabClick: PropTypes.func,
  noBlurWithoutValue: PropTypes.bool,
  preventPaste: PropTypes.bool,
  inputLength: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  floatingLabel: PropTypes.bool,
  disabledError: PropTypes.bool,
  error: PropTypes.string,
  mask: PropTypes.string,
  label: PropTypes.string,
  pattern: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)]),
  focusOnMount: PropTypes.bool,
  readonly: PropTypes.bool,
  loading: PropTypes.bool,
  formatCharacters: PropTypes.object,
  onEdit: PropTypes.func,
  onClick: PropTypes.func,
  onCopy: PropTypes.func,
  deletable: PropTypes.bool,
  onDelete: PropTypes.func,
  actionNode: PropTypes.node,
  autoComplete: PropTypes.string,
  url: PropTypes.string,
  showClearBtn: PropTypes.bool,
  color: PropTypes.oneOf(['grey', 'light-grey']),
  searchToggle: PropTypes.bool,
  goBack: PropTypes.func,
  delayedSearchValue: PropTypes.number
};
export default /*#__PURE__*/memo(Input);